import { dateOnlyOf } from '@/lib/utils/custom-date/custom-date';

/**
 * One row of an HR term's `WorkDays` (`HRWorkDay`). The work days can change during a term: every change
 * is a set of rows sharing one `EffectiveFrom` (date only) and holds until the next change. Rows without
 * `EffectiveFrom` are the term's first setting, in force from the start of the term.
 */
export interface DatedWorkDay {
  WorkDay: string;
  EffectiveFrom?: string | null;
}

/** The "YYYY-MM-DD" a row's setting starts on; '' for the term's first setting. */
export const workDayStartOf = (row: { EffectiveFrom?: string | null }): string =>
  dateOnlyOf(row.EffectiveFrom);

/** The term's work-day settings, oldest first: each with its start ('' for the first) and its rows. */
export const workDaySettingsOf = <T extends DatedWorkDay>(
  rows: readonly T[] | null | undefined
): { effectiveFrom: string; days: T[] }[] => {
  const valid = (rows ?? []).filter((row) => typeof row?.WorkDay === 'string');
  const starts = [...new Set(valid.map(workDayStartOf))].sort();
  return starts.map((effectiveFrom) => ({
    effectiveFrom,
    days: valid.filter((row) => workDayStartOf(row) === effectiveFrom),
  }));
};

/**
 * The term's work-day rows in force on `day` ("YYYY-MM-DD"), the same rule as the workflows' `workDaysOn`
 * (blocks/workflows/lib/hr-term.js): the latest setting starting on or before `day`. A day before every
 * setting takes the earliest one, so no day of a term is without work days.
 */
export const workDaysOn = <T extends DatedWorkDay>(
  rows: readonly T[] | null | undefined,
  day: string
): T[] => {
  const settings = workDaySettingsOf(rows);
  if (settings.length === 0) return [];
  const inForce = settings.filter((setting) => setting.effectiveFrom <= day);
  return (inForce.length > 0 ? inForce[inForce.length - 1] : settings[0]).days;
};

/** An HR term's period bounds (date only) and whether it is the employee's active term. */
export interface DatedHrTerm {
  HRPeriodStartDate?: string | null;
  HRPeriodEndDate?: string | null;
  IsCurrentHrYear?: boolean | null;
}

/**
 * The HR term in force on `day` ("YYYY-MM-DD") among an employee's terms, else the active one, else the
 * first; the same rule as the workflows' `hrTermOn`. Terms are read whole and matched here: a term saved
 * before its bounds were date only can miss a date-range query.
 */
export const hrTermOn = <T extends DatedHrTerm>(
  terms: readonly T[] | null | undefined,
  day: string
): T | undefined => {
  const list = terms ?? [];
  return (
    list.find((term) => {
      const start = dateOnlyOf(term.HRPeriodStartDate);
      const end = dateOnlyOf(term.HRPeriodEndDate);
      return Boolean(start && end && start <= day && day <= end);
    }) ??
    list.find((term) => term.IsCurrentHrYear === true) ??
    list[0]
  );
};
